'use client';

import React from 'react';
import { ErrorBoundaryProps, ErrorBoundaryState } from './types';

// Global React error boundary. Class component because that's still
// the only way to catch render-time errors in React (no hook
// equivalent yet).
//
// Drop in around the app tree (or any subtree) to prevent a single
// component crash from white-screening the whole site. Default
// fallback is a generic "Something went wrong" panel with a Reload
// button; consumers can override via `fallback` prop and pipe to
// Sentry via `onError`.
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError?.(error, { componentStack: errorInfo.componentStack ?? undefined });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      <div role="alert" className="mx-auto flex max-w-lg flex-col items-center gap-4 px-6 py-16 text-center">
        <h1 className="text-22 text-primary font-bold">Something went wrong</h1>
        <p className="text-14 text-gray-600">
          We hit an unexpected error rendering this page. Try reloading; if it keeps happening, contact support.
        </p>
        <button
          type="button"
          onClick={() => {
            if (typeof window !== 'undefined') window.location.reload();
          }}
          className="bg-primary text-14 hover:bg-primary/90 rounded px-4 py-2 font-semibold text-white"
        >
          Reload page
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
